import React, { useCallback, useEffect, useRef } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useCalculadoraAntecipacao } from "@/hooks/use-calculadora-antecipacao";
import { Loader2, AlertCircle, Info, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

const maquininhas = ["Cielo", "Getnet", "Stone", "PagBank", "SafraPay", "Infinite Bank", "SumUp", "MercadoPago", "Rede", "Granito", "Outros"];
const parcelasOptions = Array.from({ length: 12 }, (_, i) => i + 1);

export const CalculadoraAntecipacao = () => {
  const {
    selectedMaquininha,
    setSelectedMaquininha,
    taxaAtual,
    setTaxaAtual,
    valorAntecipar,
    setValorAntecipar,
    numeroParcelas,
    setNumeroParcelas,
    economia,
    calcularEconomia,
    custoDrCash,
    custoConcorrente,
    formatToBRL,
    currencyToNumber,
    isCalculating,
    errorMessage,
    animateResult,
    lastUpdated
  } = useCalculadoraAntecipacao();
  
  const resultadoRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    if (animateResult && resultadoRef.current && isMobile) {
      setTimeout(() => {
        resultadoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [animateResult, isMobile]);

  const handleValorChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const numberValue = Number(value) / 100; // Convert to decimal
    setValorAntecipar(numberValue);
  }, [setValorAntecipar]);

  const percentualEconomia = custoConcorrente > 0 
    ? Math.round((economia / custoConcorrente) * 100) 
    : 0;
    
  const renderTaxaLabel = (taxa: number): string => {
    if (taxa <= 2) return "Baixa";
    if (taxa <= 5) return "Média";
    if (taxa <= 8) return "Alta";
    return "Muito alta";
  };

  return (
    <TooltipProvider>
      <section id="calculadora-antecipacao" className="bg-white py-16 px-4 scroll-mt-24">
        <div className="container max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-drcash-maastricht-blue mb-3">
              Quanto Você Economiza com a Dr.Cash?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Calcule quanto você economiza ao antecipar seus recebíveis com a Dr.Cash.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div className="space-y-6 bg-[#F1F0FB] p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-md">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-drcash-maastricht-blue text-sm font-medium flex items-center gap-1.5">
                    Selecione sua maquininha atual
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Selecione a maquininha que você utiliza atualmente para processar seus pagamentos.</p>
                      </TooltipContent>
                    </Tooltip>
                  </label>
                  {selectedMaquininha && (
                    <Badge variant="outline" className={cn(
                      "bg-drcash-caribbean-green/10 text-drcash-caribbean-green border-drcash-caribbean-green/20",
                      lastUpdated === 'maquininha' && "animate-pulse"
                    )}>
                      Selecionado
                    </Badge>
                  )}
                </div>
                <Select value={selectedMaquininha} onValueChange={setSelectedMaquininha}>
                  <SelectTrigger className="bg-white border-gray-200 focus:ring-drcash-caribbean-green focus:border-drcash-caribbean-green">
                    <SelectValue placeholder="Selecione uma maquininha" />
                  </SelectTrigger>
                  <SelectContent>
                    {maquininhas.map(maquininha => (
                      <SelectItem key={maquininha} value={maquininha}>
                        {maquininha}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator className="bg-gray-200" />

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1.5">
                    <label className="text-drcash-maastricht-blue text-sm font-medium">
                      Taxa de antecipação atual
                    </label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Esta é a taxa percentual que sua operadora de cartão cobra atualmente para antecipar seus recebíveis.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={cn(
                      "bg-drcash-caribbean-green/10 text-drcash-caribbean-green border-drcash-caribbean-green/20",
                      lastUpdated === 'taxa' && "animate-pulse"
                    )}>
                      {renderTaxaLabel(taxaAtual)}
                    </Badge>
                    <span className="text-drcash-caribbean-green font-medium">
                      {taxaAtual.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <Slider 
                  min={1} 
                  max={12} 
                  step={0.1} 
                  value={[taxaAtual]} 
                  onValueChange={values => setTaxaAtual(values[0])} 
                  className="py-4"
                />
              </div>

              <Separator className="bg-gray-200" />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <label className="text-drcash-maastricht-blue text-sm font-medium">
                      Número de parcelas
                    </label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Selecione em quantas parcelas você deseja antecipar o valor.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  {numeroParcelas > 0 && (
                    <Badge variant="outline" className={cn(
                      "bg-drcash-caribbean-green/10 text-drcash-caribbean-green border-drcash-caribbean-green/20",
                      lastUpdated === 'parcelas' && "animate-pulse"
                    )}>
                      {numeroParcelas} {numeroParcelas === 1 ? 'mês' : 'meses'}
                    </Badge>
                  )}
                </div>
                <Select value={numeroParcelas.toString()} onValueChange={(value) => setNumeroParcelas(Number(value))}>
                  <SelectTrigger className="bg-white border-gray-200 focus:ring-drcash-caribbean-green focus:border-drcash-caribbean-green">
                    <SelectValue placeholder="Selecione o número de parcelas" />
                  </SelectTrigger>
                  <SelectContent>
                    {parcelasOptions.map(parcela => (
                      <SelectItem key={parcela} value={parcela.toString()}>
                        {parcela} {parcela === 1 ? 'mês' : 'meses'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator className="bg-gray-200" />

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <label className="text-drcash-maastricht-blue text-sm font-medium">
                      Valor que deseja antecipar
                    </label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Digite o valor total das vendas em cartão que você deseja antecipar, entre R$250,00 e R$1.000.000,00.</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  {valorAntecipar > 0 && (
                    <Badge variant="outline" className={cn(
                      "bg-drcash-caribbean-green/10 text-drcash-caribbean-green border-drcash-caribbean-green/20",
                      lastUpdated === 'valor' && "animate-pulse"
                    )}>
                      {valorAntecipar < 10000 ? "Baixo" : valorAntecipar < 50000 ? "Médio" : "Alto"}
                    </Badge>
                  )}
                </div>
                <Input 
                  type="text" 
                  value={formatToBRL(valorAntecipar)} 
                  onChange={handleValorChange} 
                  className="bg-white border-gray-200 focus:ring-drcash-caribbean-green focus:border-drcash-caribbean-green text-lg md:text-base font-medium" 
                  placeholder="R$ 0,00"
                  aria-label="Valor a antecipar"
                />
                {valorAntecipar < 250 && (
                  <p className="text-xs text-drcash-error flex items-center gap-1.5">
                    <AlertCircle className="h-3 w-3" />
                    Valor mínimo: R$ 250,00
                  </p>
                )}
                {valorAntecipar > 1000000 && (
                  <p className="text-xs text-drcash-error flex items-center gap-1.5">
                    <AlertCircle className="h-3 w-3" />
                    Valor máximo: R$ 1.000.000,00
                  </p>
                )}
              </div>

              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2 animate-shake">
                  <AlertCircle className="text-drcash-error h-5 w-5 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-drcash-error">{errorMessage}</p>
                </div>
              )}

              <Button 
                onClick={calcularEconomia} 
                className="w-full bg-gradient-to-r from-drcash-caribbean-green to-drcash-tiffany-blue hover:brightness-105 text-white flex items-center justify-center py-6 text-base"
                disabled={isCalculating || valorAntecipar < 250 || valorAntecipar > 1000000}
              >
                {isCalculating ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Calculando sua economia...
                  </>
                ) : (
                  'Calcular minha economia'
                )}
              </Button>
            </div>

            <div ref={resultadoRef}>
              <Card className={cn(
                "bg-drcash-sapphire-blue p-6 sm:p-8 rounded-xl shadow-lg border-2 border-transparent transition-all duration-500",
                economia > 0 && animateResult && "border-drcash-caribbean-green animate-pulse"
              )}>
                <div className="text-center space-y-6">
                  <div className="space-y-3">
                    <p className="text-white text-lg">Sua economia será:</p>
                    <p className="text-gray-300 text-sm">
                      Com base em uma antecipação de {numeroParcelas} {numeroParcelas === 1 ? 'mês' : 'meses'} totalizando {formatToBRL(valorAntecipar)}
                    </p>
                    <div className={cn(
                      "overflow-hidden transition-all duration-500",
                      animateResult && "animate-pulse"
                    )}>
                      <p className="text-4xl font-bold text-drcash-caribbean-green transition-all duration-700">
                        {formatToBRL(economia)}
                      </p>
                    </div>
                  </div>
                  
                  {economia > 0 && (
                    <p className={cn(
                      "text-lg text-drcash-caribbean-green font-medium transition-all duration-500",
                      animateResult && "animate-pulse"
                    )}>
                      Economize até {percentualEconomia}% em custos
                    </p>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 p-4 rounded-lg hover:bg-white/15 transition-all duration-300">
                      <p className="text-gray-300 mb-2 text-xs">Custo com Dr.Cash</p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1">
                            <p className="font-semibold text-white text-sm">{formatToBRL(custoDrCash)}</p>
                            <Info className="h-3 w-3 text-gray-400" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Taxa fixa de 1,9% ao mês</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="bg-white/10 p-4 rounded-lg hover:bg-white/15 transition-all duration-300">
                      <p className="text-gray-300 mb-2 text-xs">
                        Custo com {selectedMaquininha || "concorrente"}
                      </p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1">
                            <p className="font-semibold text-white text-sm">{formatToBRL(custoConcorrente)}</p>
                            <Info className="h-3 w-3 text-gray-400" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Taxa de {taxaAtual}% ao mês</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>

                  <Separator className="bg-white/20" />

                  <div className="flex flex-col items-center">
                    <p className="text-xs text-gray-300 mt-2 leading-relaxed text-center max-w-sm">
                      A Dr.Cash otimiza apenas a taxa de antecipação. Outras cobranças como MDR, taxas de parcelamento e aluguel de maquininhas continuam sendo gerenciadas e cobradas diretamente pelas suas operadoras de cartão.
                    </p>
                    
                    <Button 
                      className="mt-6 bg-drcash-caribbean-green hover:bg-drcash-caribbean-green/90 text-white"
                      size="sm"
                    >
                      Credenciar minha clínica
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </TooltipProvider>
  );
};
